import { ButtonStyled } from "components/buttons/buttons.styled";
import { usePDFViewer } from "hooks/usePDFViewer";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PdfControls,
  ViewerContainer,
  XmlContent,
} from "../salary/salary.styled";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { LocalDate } from "@js-joda/core";
import {
  ViewMode,
  ViewModeSwitcher,
} from "../salary/viewModeSwitcher.component";

type FileViewerProps = {
  fetchEndpoint: () => Promise<any>;
  filename: string;
  type: string;
  currentDate?: LocalDate;
  ListComponent?: React.ComponentType<{
    currentDate: LocalDate;
    data: any[];
    setData: React.Dispatch<React.SetStateAction<any[]>>;
  }>;
};

export const FileViewer: React.FC<FileViewerProps> = (props) => {
  const {
    viewFile,
    downloadFile,
    cleanupFile,
    fileUrl,
    fileContent,
    isLoading,
    prepareExport,
  } = usePDFViewer();
  const { t } = useTranslation();
  const [xmlContent, setXmlContent] = useState<string>("");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    setData([]);
  }, [props.currentDate]);

  const isPdf = props.type.includes("pdf");

  useEffect(() => {
    const loadFile = async () => {
      await viewFile(props.fetchEndpoint(), props.type);
    };

    loadFile();

    return () => {
      cleanupFile();
    };
  }, [props.fetchEndpoint, props.type]);

  useEffect(() => {
    if (fileContent && !isPdf) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setXmlContent(e.target.result as string);
        }
      };
      reader.readAsText(fileContent);
    }
  }, [fileContent, isPdf]);

  return (
    <ViewerContainer big={isPdf || viewMode == "xml"}>
      {isLoading && (
        <Text style={{ textAlign: "center", marginTop: 0 }}>
          {t("General.Loading")}
        </Text>
      )}
      {fileUrl ? (
        <>
          <PdfControls>
            {!isPdf && (
              <ViewModeSwitcher onChange={setViewMode} defaultMode={viewMode} />
            )}
            <ButtonStyled
              backgroundColor={Color.ModernGreen}
              onClick={() => downloadFile(props.filename)}
              notCenter
              lessPadding
              style={{ marginBottom: 20, marginLeft: 15 }}
            >
              {isPdf ? t("Salary.DownloadPdf") : t("Salary.DownloadXML")}
            </ButtonStyled>
            {!isPdf && (
              <ButtonStyled
                backgroundColor={Color.ModernGreen}
                onClick={() => {
                  if (props.currentDate) {
                    prepareExport(
                      props.currentDate?.monthValue(),
                      "SIE",
                      props.currentDate?.year()
                    );
                  }
                }}
                notCenter
                lessPadding
                style={{ marginBottom: 20, marginLeft: 15 }}
              >
                {t("Salary.DownloadSIE")}
              </ButtonStyled>
            )}
          </PdfControls>
          {isPdf ? (
            <iframe
              src={`${fileUrl}#toolbar=1`}
              width="100%"
              height="100%"
              title="PDF Viewer"
              style={{ border: "1px solid #ccc" }}
            />
          ) : viewMode == "list" && props.currentDate && props.ListComponent ? (
            <props.ListComponent
              currentDate={props.currentDate}
              data={data}
              setData={setData}
            />
          ) : (
            <XmlContent>
              <pre>{xmlContent}</pre>
            </XmlContent>
          )}
        </>
      ) : (
        <Text style={{ textAlign: "center", marginTop: 0 }}>
          {t("Salary.NoFileToShow")}
        </Text>
      )}
    </ViewerContainer>
  );
};
